<div class="punch-report">
  <div class="report-header">
    <div>
      <h2>Punch report</h2>
      <p>All punches in the project, filterable by Task. Grid is paginated locally (50 rows at a time) to stay fast.</p>
    </div>
    <div class="header-actions">
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" size="small" [loading]="loading" (onClick)="refresh()"></p-button>
      <p-button label="Download Excel" icon="pi pi-file-excel" severity="secondary" size="small" [disabled]="!filteredRows.length" (onClick)="downloadExcel()"></p-button>
    </div>
  </div>

  @if (loading) {
    <div class="loading-state"><p-progress-spinner ariaLabel="Loading punch report"></p-progress-spinner></div>
  } @else if (error) {
    <p-message severity="error" text="Could not load the punch report."></p-message>
  } @else {
    <!-- Burndown section -->
    <p-card [styleClass]="'burndown-card' + (burndownExpanded ? '' : ' collapsed')">
      <div class="burndown-header">
        <button type="button" class="burndown-toggle" (click)="toggleBurndown()" [attr.aria-expanded]="burndownExpanded">
          <i class="pi" [ngClass]="burndownExpanded ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
          <h3>Burndown - {{ burndownRangeLabel }}</h3>
        </button>
        @if (burndownExpanded) {
          <p-selectButton
            [options]="granularityOptions"
            [ngModel]="granularity"
            (ngModelChange)="onGranularityChange($event)"
            optionLabel="label"
            optionValue="value"
          ></p-selectButton>
        }
      </div>
      @if (burndownExpanded) {
        <div class="burndown-charts">
          <div class="burndown-chart burndown-left">
            <span class="axis-label">Left axis - Open punches</span>
            <ngx-charts-line-chart
              [results]="leftChartData"
              [xAxis]="true"
              [yAxis]="true"
              [showXAxisLabel]="true"
              xAxisLabel="Period"
              [showYAxisLabel]="true"
              yAxisLabel="Open punches"
              [scheme]="$any(leftColorScheme)"
              [autoScale]="true"
              [legend]="true"
              [legendPosition]="legendPosition"
            >
            </ngx-charts-line-chart>
          </div>
          <div class="burndown-chart burndown-right">
            <span class="axis-label">Right axis - Punch activity (A / B)</span>
            <div class="chart-scroll-x">
              <ngx-charts-bar-vertical-2d
                [style.width.px]="rightChartWidthPx"
                [style.min-width.%]="rightChartWidthPx ? null : 100"
                [results]="rightChartData"
                [xAxis]="true"
                [yAxis]="true"
                [showXAxisLabel]="true"
                xAxisLabel="Period"
                [showYAxisLabel]="true"
                yAxisLabel="Count"
                [scheme]="$any(rightColorScheme)"
                [legend]="true"
                [legendPosition]="legendPosition"
                [groupPadding]="rightGroupPadding"
                [barPadding]="2"
              >
              </ngx-charts-bar-vertical-2d>
            </div>
          </div>
        </div>
      }
    </p-card>

    <!-- Filters -->
    <div class="filter-bar">
      <div class="filter-field">
        <label for="task-filter">Task</label>
        <p-select
          id="task-filter"
          [options]="taskOptions"
          [ngModel]="selectedTaskId"
          (ngModelChange)="selectedTaskId = $event; onTaskFilterChange()"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          filterBy="label"
          placeholder="All tasks"
          [style]="{ minWidth: '260px' }"
        ></p-select>
      </div>
      <div class="filter-field global-search">
        <label for="global-search-input">Search</label>
        <p-iconField iconPosition="left">
          <p-inputIcon styleClass="pi pi-search"></p-inputIcon>
          <input
            id="global-search-input"
            pInputText
            type="text"
            [(ngModel)]="globalFilterValue"
            (ngModelChange)="dt.filterGlobal($event, 'contains')"
            placeholder="Search all columns..."
          />
        </p-iconField>
      </div>
      <div class="filter-field row-count-field">
        <label>&nbsp;</label>
        <div class="row-count">{{ visibleRowCount }} / {{ filteredRows.length }} punches</div>
      </div>
    </div>

    @if (activeFilters.length) {
      <div class="active-filters-bar">
        <span class="active-filters-label">Filters:</span>
        @for (chip of activeFilters; track chip.field) {
          <span class="filter-chip">
            <strong>{{ chip.label }}:</strong> {{ chip.valueText }}
            <button type="button" class="chip-remove" (click)="clearFilter(chip)" [attr.aria-label]="'Remove ' + chip.label + ' filter'">
              <i class="pi pi-times"></i>
            </button>
          </span>
        }
        <button type="button" class="clear-all-link" (click)="clearAllFilters()">Clear all</button>
      </div>
    }

    <!-- Grid -->
    <p-table
      #dt
      [value]="filteredRows"
      dataKey="id"
      [paginator]="true"
      [rows]="rows"
      [rowsPerPageOptions]="rowsPerPageOptions"
      [scrollable]="true"
      scrollHeight="calc(100vh - 380px)"
      [tableStyle]="{ 'min-width': '1650px' }"
      [resizableColumns]="true"
      columnResizeMode="expand"
      (onColResize)="onColumnResize($event)"
      (onFilter)="onTableFilter($event)"
      [globalFilterFields]="[
        'taskNumber',
        'cpNumber',
        'mcpNumber',
        'tagNumber',
        'checkSheetName',
        'checkSheetItemName',
        'category',
        'description',
        'notes',
        'raisedByCompany',
        'assignedCompany',
        'assignedUserName',
        'enteredByUserName',
        'clearedByUserName',
        'acceptedByUserName',
      ]"
      [showGridlines]="true"
      styleClass="p-datatable-sm punch-report-table"
      responsiveLayout="scroll"
    >
      <ng-template pTemplate="header">
        <tr>
          <th pResizableColumn pSortableColumn="taskNumber" [attr.data-field]="'taskNumber'" [style.width.px]="columnWidths['taskNumber']" style="min-width: 75px">
            <div class="th-content">
              <span>Task <p-sortIcon field="taskNumber" /></span>
              <app-excel-column-filter field="taskNumber" [options]="columnFilterOptions['taskNumber']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="cpNumber" [attr.data-field]="'cpNumber'" [style.width.px]="columnWidths['cpNumber']" style="min-width: 70px">
            <div class="th-content">
              <span>CP <p-sortIcon field="cpNumber" /></span>
              <app-excel-column-filter field="cpNumber" [options]="columnFilterOptions['cpNumber']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="mcpNumber" [attr.data-field]="'mcpNumber'" [style.width.px]="columnWidths['mcpNumber']" style="min-width: 70px">
            <div class="th-content">
              <span>MCP <p-sortIcon field="mcpNumber" /></span>
              <app-excel-column-filter field="mcpNumber" [options]="columnFilterOptions['mcpNumber']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="systemNumber" [attr.data-field]="'systemNumber'" [style.width.px]="columnWidths['systemNumber']" style="min-width: 60px">
            <div class="th-content">
              <span>System <p-sortIcon field="systemNumber" /></span>
              <app-excel-column-filter field="systemNumber" [options]="columnFilterOptions['systemNumber']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="subSystemNumber" [attr.data-field]="'subSystemNumber'" [style.width.px]="columnWidths['subSystemNumber']" style="min-width: 70px">
            <div class="th-content">
              <span>SubSystem <p-sortIcon field="subSystemNumber" /></span>
              <app-excel-column-filter field="subSystemNumber" [options]="columnFilterOptions['subSystemNumber']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="tagNumber" [attr.data-field]="'tagNumber'" [style.width.px]="columnWidths['tagNumber']" style="min-width: 90px">
            <div class="th-content">
              <span>Tag <p-sortIcon field="tagNumber" /></span>
              <app-excel-column-filter field="tagNumber" [options]="columnFilterOptions['tagNumber']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="checkSheetName" [attr.data-field]="'checkSheetName'" [style.width.px]="columnWidths['checkSheetName']" style="min-width: 100px">
            <div class="th-content">
              <span>CheckSheet <p-sortIcon field="checkSheetName" /></span>
              <app-excel-column-filter field="checkSheetName" [options]="columnFilterOptions['checkSheetName']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="checkSheetItemName" [attr.data-field]="'checkSheetItemName'" [style.width.px]="columnWidths['checkSheetItemName']" style="min-width: 115px">
            <div class="th-content">
              <span>Item <p-sortIcon field="checkSheetItemName" /></span>
              <app-excel-column-filter field="checkSheetItemName" [options]="columnFilterOptions['checkSheetItemName']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="punchNumber" [attr.data-field]="'punchNumber'" [style.width.px]="columnWidths['punchNumber']" style="min-width: 55px">
            <div class="th-content">
              <span>Punch # <p-sortIcon field="punchNumber" /></span>
              <app-excel-column-filter field="punchNumber" type="text"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="category" [attr.data-field]="'category'" [style.width.px]="columnWidths['category']" style="min-width: 60px">
            <div class="th-content">
              <span>Category <p-sortIcon field="category" /></span>
              <app-excel-column-filter field="category" [options]="columnFilterOptions['category']" minWidth="180px"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn [attr.data-field]="'description'" [style.width.px]="columnWidths['description']" style="min-width: 130px">
            <div class="th-content">
              <span>Description</span>
              <app-excel-column-filter field="description" type="text"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="due" [attr.data-field]="'due'" [style.width.px]="columnWidths['due']" style="min-width: 65px">Due <p-sortIcon field="due" /></th>
          <th pResizableColumn pSortableColumn="raisedByCompany" [attr.data-field]="'raisedByCompany'" [style.width.px]="columnWidths['raisedByCompany']" style="min-width: 80px">
            <div class="th-content">
              <span>RaisedBy <p-sortIcon field="raisedByCompany" /></span>
              <app-excel-column-filter field="raisedByCompany" [options]="columnFilterOptions['raisedByCompany']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="assignedUserName" [attr.data-field]="'assignedUserName'" [style.width.px]="columnWidths['assignedUserName']" style="min-width: 90px">
            <div class="th-content">
              <span>AssignedTo <p-sortIcon field="assignedUserName" /></span>
              <app-excel-column-filter field="assignedUserName" [options]="columnFilterOptions['assignedUserName']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="enteredByUserName" [attr.data-field]="'enteredByUserName'" [style.width.px]="columnWidths['enteredByUserName']" style="min-width: 85px">
            <div class="th-content">
              <span>EnteredBy <p-sortIcon field="enteredByUserName" /></span>
              <app-excel-column-filter field="enteredByUserName" [options]="columnFilterOptions['enteredByUserName']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="enteredByTimestamp" [attr.data-field]="'enteredByTimestamp'" [style.width.px]="columnWidths['enteredByTimestamp']" style="min-width: 75px">EnteredDate <p-sortIcon field="enteredByTimestamp" /></th>
          <th pResizableColumn pSortableColumn="clearedByUserName" [attr.data-field]="'clearedByUserName'" [style.width.px]="columnWidths['clearedByUserName']" style="min-width: 85px">
            <div class="th-content">
              <span>ClearedBy <p-sortIcon field="clearedByUserName" /></span>
              <app-excel-column-filter field="clearedByUserName" [options]="columnFilterOptions['clearedByUserName']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="clearedByTimestamp" [attr.data-field]="'clearedByTimestamp'" [style.width.px]="columnWidths['clearedByTimestamp']" style="min-width: 75px">ClearedDate <p-sortIcon field="clearedByTimestamp" /></th>
          <th pResizableColumn pSortableColumn="acceptedByUserName" [attr.data-field]="'acceptedByUserName'" [style.width.px]="columnWidths['acceptedByUserName']" style="min-width: 85px">
            <div class="th-content">
              <span>AcceptedBy <p-sortIcon field="acceptedByUserName" /></span>
              <app-excel-column-filter field="acceptedByUserName" [options]="columnFilterOptions['acceptedByUserName']"></app-excel-column-filter>
            </div>
          </th>
          <th pResizableColumn pSortableColumn="acceptedByTimestamp" [attr.data-field]="'acceptedByTimestamp'" [style.width.px]="columnWidths['acceptedByTimestamp']" style="min-width: 75px">AcceptedDate <p-sortIcon field="acceptedByTimestamp" /></th>
          <th pResizableColumn pSortableColumn="open" [attr.data-field]="'open'" [style.width.px]="columnWidths['open']" style="min-width: 55px">
            <div class="th-content">
              <span>Status <p-sortIcon field="open" /></span>
              <app-excel-column-filter field="open" [options]="statusFilterOptions" minWidth="140px" [showSearch]="false"></app-excel-column-filter>
            </div>
          </th>
        </tr>
        <!-- Freetext quick-filter row: a simple "contains" search box per column, always visible. -->
        <tr class="filter-row">
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'taskNumber', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'cpNumber', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'mcpNumber', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'systemNumber', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'subSystemNumber', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'tagNumber', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'checkSheetName', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'checkSheetItemName', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'punchNumber', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'category', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'description', 'contains')" /></p-iconField></th>
          <th></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'raisedByCompany', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'assignedUserName', 'contains')" /></p-iconField></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'enteredByUserName', 'contains')" /></p-iconField></th>
          <th></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'clearedByUserName', 'contains')" /></p-iconField></th>
          <th></th>
          <th><p-iconField iconPosition="left"><p-inputIcon styleClass="pi pi-search"></p-inputIcon><input pInputText type="text" placeholder="Search" (input)="dt.filter($any($event.target).value, 'acceptedByUserName', 'contains')" /></p-iconField></th>
          <th></th>
          <th></th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr [class]="row.rowClasses">
          <td>{{ row.taskNumber }}</td>
          <td>{{ row.cpNumber }}</td>
          <td>{{ row.mcpNumber }}</td>
          <td>{{ row.systemNumber }}</td>
          <td>{{ row.subSystemNumber }}</td>
          <td>{{ row.tagNumber }}</td>
          <td>{{ row.checkSheetNumber }} {{ row.checkSheetName }}</td>
          <td>{{ row.checkSheetItemNumber | number: '1.0-1' }} {{ row.checkSheetItemName }}</td>
          <td>{{ row.punchNumber }}</td>
          <td><app-status-circle [status]="row.category" [useSmall]="true"></app-status-circle></td>
          <td class="wrap-cell">{{ row.description }}</td>
          <td>{{ row.due | date: 'dd.MM.yyyy' }}</td>
          <td>{{ row.raisedByCompany }}</td>
          <td>{{ row.assignedUserName }}</td>
          <td>{{ row.enteredByUserName }}</td>
          <td>{{ row.enteredByTimestamp | date: 'dd.MM.yyyy' }}</td>
          <td>{{ row.clearedByUserName }}</td>
          <td>{{ row.clearedByTimestamp | date: 'dd.MM.yyyy' }}</td>
          <td>{{ row.acceptedByUserName }}</td>
          <td>{{ row.acceptedByTimestamp | date: 'dd.MM.yyyy' }}</td>
          <td><app-status-circle [status]="getEffectiveStatus(row)" [useSmall]="true"></app-status-circle></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="20">No punches found.</td>
        </tr>
      </ng-template>
    </p-table>
  }
</div>


